/**
 * 📮 개발요청 게시판 (v2.244.0 · 2026-10-13 수출입 화면 안 탭 → v2.274.0 전 메뉴 · 전 직원)
 *
 * v2.274.0 사용자(10-18): 「개발요청 버튼을 메뉴 어디를 이동하든지 우측상단에 … 누르면 팝업으로 입력창이 나와서
 *   글도 적고 파일·사진 첨부 … 작성된 건들은 지금의 개발계획 위치에 누적으로 게시 · 처리상태 표시 · 거기서 작성·수정도 ·
 *   처리범위는 전 메뉴 · 계획까지만 세우고 답글로 · 충돌있거나 위험한건 거부 · 애매한건 보류 · 진행할만한건 처리중 ·
 *   요청들이 모이면 내가 한번에 진행시작 · 접근권한은 뷰어계정까지 포함」
 *   · 쓰고 보는 사람 = 전 직원(조회 전용 포함 · 서버 가드) · 상태 바꾸기 = 관리자만(+ 🤖 루틴)
 *   · 쓴 사람은 「요청」·「보류」일 때만 고치고 지운다 — 「처리중」(계획을 세움) 뒤에는 댓글로 덧붙인다
 *   · 본문 = 서식 편집기(캡처 Ctrl+V) · 파일·사진은 **쓰는 창에서 바로** 붙인다(올리기를 누르면 함께 올라간다)
 *   · 🤖 댓글 = 부장님 PC 의 루틴(ops/devreq-bot.mjs) — 사람 계정이 아니다
 *   · 쓰는 창(DevRequestModal)은 오른쪽 위 단추(DevRequestFab)와 이 게시판이 **같은 것**을 쓴다
 * ⛔입력 창은 ESC·배경 클릭으로 안 닫힌다(v2.231.0 규칙) · 적다 만 글이 있으면 닫을 때 묻는다.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DraggableModal } from './DraggableModal';
import { RichEditor } from './RichEditor';
import { AttachmentSection, type AcceptSpec } from './AttachmentSection';
import { SegmentedControl } from './ui/SegmentedControl';
import { useDirtyClose } from '../lib/useDirtyClose';
import { useAuth } from '../lib/auth-context';
import { uploadAttachment } from '../lib/attachment-api';
import { EMPTY_DOC, DEV_PLAN_DOC_LIMITS, docForEdit, docForSave, docIsEmpty, textToRichDoc, type RichDoc } from '../lib/rich-doc';
import * as devReqApi from '../lib/dev-request-api';
import {
  DEV_REQUEST_STATUSES, DEVREQ_ATTACH_KIND, STATUS_META,
  type DevRequestDetail, type DevRequestRow, type DevRequestStatus,
} from '../lib/dev-request-api';
import { devMenuKeyForPath, devMenuLabel, devMenuOptions } from '../lib/dev-request-menus';
import { LoadingLines } from './ui/Skeleton';

const ACCEPT: AcceptSpec = {
  exts: /\.(pdf|xlsx|xlsm|xls|csv|png|jpe?g|gif|webp|bmp)$/i,
  attr: '.pdf,.xlsx,.xlsm,.xls,.csv,.png,.jpg,.jpeg,.gif,.webp,.bmp',
  label: 'PDF·엑셀·사진',
};

/** 게시판 주소 — 옛 「개발 계획」 메뉴 자리(주소는 그대로 둔다 · 즐겨찾기가 깨지지 않게) */
export const DEV_REQUEST_BOARD_PATH = '/dev-plan';

function fmtDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function StatusChip({ status }: { status: DevRequestStatus }) {
  const m = STATUS_META[status];
  return (
    <span className={`px-1.5 py-0.5 rounded border text-xs whitespace-nowrap ${m.chip}`} title={m.hint}>
      {m.icon} {status}
    </span>
  );
}

function MenuChip({ menuKey }: { menuKey: string }) {
  const label = devMenuLabel(menuKey);
  return (
    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs whitespace-nowrap max-w-[11rem] truncate" title={`메뉴: ${label}`}>
      {label}
    </span>
  );
}

/** 글자 속 주소(PR 링크 등)는 눌러서 열리게 — HTML 은 심지 않는다(글자를 쪼개 React 가 그린다) */
function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s<>"')|]+)/g);
  return (
    <>
      {parts.map((p, i) => (/^https?:\/\//.test(p)
        ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">{p}</a>
        : <span key={i}>{p}</span>))}
    </>
  );
}

const isTableLine = (s: string) => /^\s*\|.*\|\s*$/.test(s);
const isSepLine = (s: string) => /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/.test(s);
const cellsOf = (s: string) => s.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

/**
 * 댓글 글자 — 줄바꿈 그대로 · 🤖 루틴은 「자연어 + 표」로 보고하므로 `| 칸 | 칸 |` 묶음(둘째 줄이 |---|)은 **표로** 그린다.
 *   그 밖의 기호(**굵게** 등)는 해석하지 않는다 — 사람이 쓴 글이 엉뚱하게 바뀌지 않게.
 */
function CommentText({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: ({ kind: 'text'; text: string } | { kind: 'table'; head: string[]; rows: string[][] })[] = [];
  for (let i = 0; i < lines.length;) {
    if (isTableLine(lines[i]!) && i + 1 < lines.length && isSepLine(lines[i + 1]!)) {
      const head = cellsOf(lines[i]!);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableLine(lines[i]!)) { rows.push(cellsOf(lines[i]!)); i += 1; }
      blocks.push({ kind: 'table', head, rows });
    } else {
      const last = blocks[blocks.length - 1];
      if (last?.kind === 'text') last.text += `\n${lines[i]}`;
      else blocks.push({ kind: 'text', text: lines[i]! });
      i += 1;
    }
  }
  return (
    <div className="text-sm text-slate-800 space-y-1.5">
      {blocks.map((b, i) => (b.kind === 'text'
        ? <div key={i} className="whitespace-pre-wrap break-words"><Linkified text={b.text} /></div>
        : (
          <div key={i} className="overflow-x-auto">
            <table className="text-xs border-collapse">
              <thead><tr>{b.head.map((h, j) => <th key={j} className="border border-slate-300 bg-slate-100 px-2 py-1 text-left font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {b.rows.map((r, k) => (
                  <tr key={k}>{b.head.map((_, j) => <td key={j} className="border border-slate-200 px-2 py-1 align-top"><Linkified text={r[j] ?? ''} /></td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        )))}
    </div>
  );
}

type Filter = 'all' | DevRequestStatus;

/** 게시판 — 전 메뉴의 요청. 주소 `?menu=/sales` 로 메뉴를, `?id=12` 로 한 건을 바로 연다 */
export function DevRequestBoard() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<Filter>('all');
  const menuFilter = params.get('menu') ?? 'all';
  const idParam = Number(params.get('id') ?? '');
  /** 열린 창 — null=닫힘 · 'new'=새 요청 · 숫자=그 요청 */
  const [open, setOpen] = useState<'new' | number | null>(Number.isInteger(idParam) && idParam > 0 ? idParam : null);
  // 게시판을 보는 중에 「게시판에서 보기」(?id=) 로 다시 들어와도 그 요청이 열리게
  useEffect(() => {
    if (Number.isInteger(idParam) && idParam > 0) setOpen((cur) => (cur === idParam ? cur : idParam));
  }, [idParam]);

  const listQ = useQuery({
    queryKey: ['dev-requests', 'all'],
    queryFn: () => devReqApi.listRequests(),
    staleTime: 0,
  });
  const rows = listQ.data ?? [];
  const byMenu = menuFilter === 'all' ? rows : rows.filter((r) => r.menuKey === menuFilter);
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: byMenu.length };
    for (const s of DEV_REQUEST_STATUSES) c[s] = byMenu.filter((r) => r.status === s).length;
    return c;
  }, [byMenu]);
  const shown = filter === 'all' ? byMenu : byMenu.filter((r) => r.status === filter);
  /** 메뉴 고르기 — 요청이 하나라도 있는 메뉴만(건수와 함께) */
  const menuChoices = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.menuKey, (m.get(r.menuKey) ?? 0) + 1);
    if (menuFilter !== 'all' && !m.has(menuFilter)) m.set(menuFilter, 0);
    return [...m.entries()].sort((a, b) => devMenuLabel(a[0]).localeCompare(devMenuLabel(b[0]), 'ko'));
  }, [rows, menuFilter]);

  const setMenuFilter = (k: string) => {
    const next = new URLSearchParams(params);
    if (k === 'all') next.delete('menu'); else next.set('menu', k);
    setParams(next, { replace: true });
  };
  const openReq = (v: 'new' | number | null) => {
    setOpen(v);
    const next = new URLSearchParams(params);
    if (typeof v === 'number') next.set('id', String(v)); else next.delete('id');
    setParams(next, { replace: true });
  };

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['dev-requests'] }); };

  return (
    <div>
      <div className="rounded border border-sky-200 bg-sky-50/50 px-3 py-2 mb-3 text-sm text-slate-700 flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <b>📮 개발요청</b> — 포털에서 바꾸고 싶은 점을 글·캡처(Ctrl+V)·파일로 남겨 주세요. 어느 화면에서든 <b>오른쪽 위 [📮 개발요청]</b> 단추로도 씁니다.
          <div className="text-xs text-slate-500 mt-0.5">
            평일 9~19시 2시간마다 🤖 가 검토해 계획을 답글로 남깁니다 — 진행할 만하면 「🛠 처리중」, 정할 것이 있으면 「⏸ 보류」(질문),
            다른 기능과 충돌하거나 위험하면 「⛔ 거부」. 개발은 부장님이 요청을 모아서 한 번에 시작합니다.
          </div>
        </div>
        <button type="button" onClick={() => openReq('new')}
          className="btn btn-primary">+ 새 요청</button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-2">
        <SegmentedControl ariaLabel="개발요청 상태별 보기" value={filter} onChange={setFilter}
          options={(['all', ...DEV_REQUEST_STATUSES] as Filter[]).map((f) => ({ key: f, label: f === 'all' ? '전체' : `${STATUS_META[f].icon} ${f}`, count: counts[f] ?? 0 }))} />
        <select value={menuFilter} onChange={(e) => setMenuFilter(e.target.value)}
          aria-label="메뉴별 보기" className="ui-input ui-input-sm">
          <option value="all">모든 메뉴 ({rows.length})</option>
          {menuChoices.map(([k, n]) => <option key={k} value={k}>{devMenuLabel(k)} ({n})</option>)}
        </select>
      </div>

      <div className="border border-slate-200 rounded bg-white divide-y divide-slate-100">
        {listQ.isPending && <div className="py-10 text-center text-sm text-slate-400"><LoadingLines /></div>}
        {listQ.isError && <div className="py-10 text-center text-sm text-rose-500">불러오기 실패: {(listQ.error as Error).message}</div>}
        {listQ.data && shown.length === 0 && (
          <div className="py-10 text-center text-sm text-slate-400">
            {rows.length === 0 ? '아직 올라온 요청이 없습니다. [+ 새 요청] 으로 시작하세요.' : '이 조건의 요청이 없습니다.'}
          </div>
        )}
        {shown.map((r) => <RequestLine key={r.id} r={r} onOpen={() => openReq(r.id)} />)}
      </div>

      {open !== null && (
        <DevRequestModal
          key={String(open)}
          id={open === 'new' ? null : open}
          defaultMenuKey={menuFilter !== 'all' ? menuFilter : undefined}
          onClose={() => openReq(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function RequestLine({ r, onOpen }: { r: DevRequestRow; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen}
      className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 min-w-0 ${r.status === '처리완료' || r.status === '거부' ? 'opacity-70' : ''}`}>
      <span className="font-mono text-xs text-slate-500 shrink-0">#{r.id}</span>
      <StatusChip status={r.status} />
      <span className="shrink-0 hidden sm:inline"><MenuChip menuKey={r.menuKey} /></span>
      <span className="flex-1 min-w-0 truncate text-sm font-medium text-slate-800" title={r.title}>{r.title}</span>
      {r.lastCommentIsBot && <span className="shrink-0 text-xs px-1 rounded bg-sky-100 text-sky-700" title="마지막 댓글이 🤖 루틴의 답입니다">🤖 답변</span>}
      <span className="shrink-0 text-xs text-slate-500 hidden sm:inline">{r.createdByName ?? r.createdBy ?? '-'}</span>
      <span className="shrink-0 text-xs text-slate-500 whitespace-nowrap" title={`올린 때 ${fmtDateTime(r.createdAt)} · 마지막 소식 ${fmtDateTime(r.lastActivityAt)}`}>
        {fmtDateTime(r.lastActivityAt).slice(5)}
      </span>
      <span className="shrink-0 text-xs text-slate-500 w-14 text-right whitespace-nowrap">💬{r.commentCount}{r.attachmentCount > 0 ? ` 📎${r.attachmentCount}` : ''}</span>
    </button>
  );
}

/* ───────────────────────── 요청 창 (새로 쓰기 · 보기 · 고치기 · 댓글) ─────────────────────────
 * 게시판과 오른쪽 위 단추(DevRequestFab)가 함께 쓴다.
 *   · id=null 이면 새로 쓰기 — 올리면 **같은 창이 그 요청 보기로** 바뀐다(창을 다시 그리지 않아 첨부 실패 안내가 남는다)
 *   · defaultMenuKey = 새로 쓸 때 미리 골라 둘 메뉴(없으면 지금 화면에서 찾는다)
 *   · showBoardLink = 게시판 밖(오른쪽 위 단추)에서 열었을 때 「게시판에서 보기」 줄
 */
export function DevRequestModal({ id, defaultMenuKey, initialTitle, initialText, onClose, onChanged, showBoardLink = false }: {
  id: number | null; defaultMenuKey?: string;
  /** 새로 쓰기를 미리 채운다 — 💬 사용 문의 창의 「개발요청으로 남기기」(v2.275.0 · 질문·답을 옮겨 적는다) */
  initialTitle?: string; initialText?: string;
  onClose: () => void; onChanged: () => void; showBoardLink?: boolean;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { markDirty, resetDirty, requestClose } = useDirtyClose(onClose);
  const [curId, setCurId] = useState<number | null>(id);
  const isNew = curId === null;

  const detailQ = useQuery({
    queryKey: ['dev-request', curId],
    queryFn: () => devReqApi.getRequest(curId!),
    enabled: !isNew,
    staleTime: 0,
  });
  const d = detailQ.data;

  const menuOptions = useMemo(() => devMenuOptions(user), [user]);
  const [menuKey, setMenuKey] = useState<string>(() => {
    const k = defaultMenuKey ?? devMenuKeyForPath(window.location.pathname);
    return menuOptions.some((o) => o.key === k) ? k : 'common';
  });
  const [editing, setEditing] = useState(isNew);
  const [title, setTitle] = useState(() => (isNew ? (initialTitle ?? '').slice(0, 200) : ''));
  const [draft, setDraft] = useState<RichDoc>(() => (isNew && initialText ? textToRichDoc(initialText) : EMPTY_DOC));
  const [files, setFiles] = useState<File[]>([]);
  // 미리 채운 글이 있으면 처음부터 「적다 만 글」 — 닫을 때 한 번 묻는다
  const [touched, setTouched] = useState(() => isNew && !!(initialTitle || initialText));
  const [comment, setComment] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** 닫을 때 물어야 하는 것 — 적다 만 본문·제목·고른 파일 · 적다 만 댓글. 상태·첨부(보기 화면)는 누르는 순간 저장되므로 아니다 */
  const unsaved = (editing && (touched || files.length > 0)) || comment.trim() !== '';
  useEffect(() => { if (unsaved) markDirty(); else resetDirty(); }, [unsaved, markDirty, resetDirty]);

  const reload = () => {
    void qc.invalidateQueries({ queryKey: ['dev-request', curId] });
    onChanged();
  };

  const createM = useMutation({
    mutationFn: async () => {
      const r = await devReqApi.createRequest({ menuKey, title: title.trim(), doc: docForSave(draft) });
      // 고른 파일 — 요청이 생긴 뒤 하나씩 올린다. 실패해도 요청은 남는다(보기 화면 첨부함에서 다시 붙인다)
      const failedNames: string[] = [];
      for (const f of files) {
        try { await uploadAttachment('devreq', String(r.id), DEVREQ_ATTACH_KIND, f); } catch { failedNames.push(f.name); }
      }
      return { id: r.id, failedNames };
    },
    onSuccess: ({ id: newId, failedNames }) => {
      setTouched(false); setFiles([]); resetDirty(); setErr(null);
      setNotice(failedNames.length === 0
        ? '요청을 올렸습니다 — 다음 검토(평일 2시간마다) 때 🤖 가 답글을 남깁니다.'
        : null);
      if (failedNames.length > 0) {
        setErr(`요청은 올라갔지만 파일 ${failedNames.length}개를 붙이지 못했습니다 — 아래 첨부함에서 다시 붙여 주세요: ${failedNames.join(', ')}`);
      }
      setEditing(false);
      setCurId(newId);
      onChanged();
    },
    onError: (e) => setErr((e as Error).message),
  });
  const updateM = useMutation({
    mutationFn: () => devReqApi.updateRequest(curId!, { title: title.trim(), doc: docForSave(draft) }),
    onSuccess: () => { setEditing(false); setTouched(false); setErr(null); reload(); },
    onError: (e) => setErr((e as Error).message),
  });
  const deleteM = useMutation({
    mutationFn: () => devReqApi.deleteRequest(curId!),
    onSuccess: () => { onChanged(); onClose(); },
    onError: (e) => setErr((e as Error).message),
  });
  const commentM = useMutation({
    mutationFn: () => devReqApi.addComment(curId!, comment.trim()),
    onSuccess: () => { setComment(''); setErr(null); reload(); },
    onError: (e) => setErr((e as Error).message),
  });
  const statusM = useMutation({
    mutationFn: (p: { status: DevRequestStatus; note: string }) => devReqApi.setStatus(curId!, p.status, p.note),
    onSuccess: () => { setErr(null); reload(); },
    onError: (e) => setErr((e as Error).message),
  });

  const startEdit = (x: DevRequestDetail) => {
    setTitle(x.title);
    setDraft(docForEdit(x.doc, (imageId) => devReqApi.imageUrl(x.id, imageId)));
    setTouched(false);
    setErr(null);
    setNotice(null);
    setEditing(true);
  };

  const save = () => {
    if (!title.trim()) { setErr('제목을 적어 주세요'); return; }
    if (docIsEmpty(draft)) { setErr('요청 내용을 적어 주세요 — 무엇을 어떻게 바꾸고 싶은지 (캡처는 Ctrl+V)'); return; }
    if (isNew) createM.mutate(); else updateM.mutate();
  };

  const changeStatus = (s: DevRequestStatus) => {
    if (!d || s === d.status) return;
    let note = '';
    if (s === '거부') {
      const reason = window.prompt('거부 사유를 적어 주세요 (댓글로 남습니다)');
      if (reason === null) return;
      if (!reason.trim()) { setErr('거부 사유를 적어 주세요'); return; }
      note = `⛔ 거부 — ${reason.trim()}`;
    } else if (s === '보류') {
      const why = window.prompt('보류하는 이유·물어볼 것을 적어 주세요 (댓글로 남습니다 · 비워도 됩니다)');
      if (why === null) return;
      note = why.trim() ? `⏸ 보류 — ${why.trim()}` : '';
    } else if (!window.confirm(`상태를 「${s}」(으)로 바꿀까요? (바꾼 사실이 댓글로 남습니다)`)) {
      return;
    }
    statusM.mutate({ status: s, note });
  };

  const busy = createM.isPending || updateM.isPending;

  return (
    <DraggableModal onClose={requestClose} initialWidth={860} initialHeight={760} closeOnBackdrop={false} closeOnEsc={false}>
      <div className="dm-drag-handle cursor-move flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
        <h2 className="ui-section-title truncate">
          📮 {isNew ? '새 개발요청' : d ? `#${d.id} ${d.title}` : `#${curId}`}
        </h2>
        <button type="button" onClick={requestClose} className="ui-close">×</button>
      </div>

      <div className="p-4 space-y-3">
        {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 whitespace-pre-wrap">{err}</div>}
        {notice && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">{notice}</div>}
        {!isNew && detailQ.isPending && <div className="text-sm text-slate-400 text-center py-6"><LoadingLines /></div>}
        {!isNew && detailQ.isError && <div className="text-sm text-red-500 text-center py-6">오류: {(detailQ.error as Error).message}</div>}

        {/* 머리 — 상태 · 메뉴 · 쓴 사람 · 단추 */}
        {d && !editing && (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <StatusChip status={d.status} />
            <MenuChip menuKey={d.menuKey} />
            <span className="text-slate-600">{d.createdByName ?? d.createdBy ?? '-'}</span>
            <span className="text-slate-400">{fmtDateTime(d.createdAt)}</span>
            {d.updatedAt !== d.createdAt && <span className="text-slate-400">· 고침 {fmtDateTime(d.updatedAt)}</span>}
            <span className="flex items-center gap-2 ml-auto shrink-0">
              {d.canSetStatus && (
                <select value={d.status} disabled={statusM.isPending}
                  onChange={(e) => changeStatus(e.target.value as DevRequestStatus)}
                  title="상태 바꾸기 (관리자) — 바꾼 사실이 댓글로 남습니다"
                  className="ui-input ui-input-sm">
                  {DEV_REQUEST_STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].icon} {s}</option>)}
                </select>
              )}
              {d.canEdit && (
                <>
                  <button type="button" onClick={() => startEdit(d)}
                    className="btn btn-secondary btn-sm">수정</button>
                  <button type="button"
                    onClick={() => { if (window.confirm('이 요청을 지웁니다(댓글·그림·첨부 포함). 되돌릴 수 없습니다.')) deleteM.mutate(); }}
                    className="btn btn-danger btn-sm">삭제</button>
                </>
              )}
            </span>
          </div>
        )}

        {/* 쓰기·고치기 */}
        {editing && (
          <div className="rounded border border-blue-300 bg-blue-50/40 p-3 space-y-2">
            {isNew && (
              <label className="flex items-center gap-2 text-sm">
                <span className="text-slate-600 shrink-0">메뉴</span>
                <select value={menuKey} onChange={(e) => { setMenuKey(e.target.value); setTouched(true); }}
                  aria-label="어느 메뉴의 요청인가" className="ui-input ui-input-sm min-w-0 flex-1 sm:flex-none sm:w-64">
                  {menuOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <span className="text-xs text-slate-500 hidden sm:inline">지금 보던 화면이 미리 골라져 있습니다</span>
              </label>
            )}
            <input value={title} maxLength={200} autoFocus={isNew}
              onChange={(e) => { setTitle(e.target.value); setTouched(true); }}
              placeholder="제목 — 예: 재고 현황 표에 입고일 칸 추가"
              className="ui-input w-full" />
            <RichEditor
              label="개발요청"
              value={draft}
              onChange={(v) => { setTouched(true); setDraft(v); }}
              limits={DEV_PLAN_DOC_LIMITS}
              minHeight={180}
              placeholder="무엇이 불편한지 · 어떻게 바뀌면 좋겠는지 적어 주세요. 화면 캡처는 Ctrl+V 로 이 자리에 바로 붙여넣을 수 있습니다."
            />
            {isNew && (
              <AttachmentSection mode="pending" files={files} onChange={setFiles} title="파일·사진 첨부" accept={ACCEPT} />
            )}
            <div className="text-xs text-slate-500">
              ⓘ 모든 직원(조회 전용 포함)이 이 게시판을 봅니다 — 금액·개인정보가 찍힌 캡처는 가려서 올려 주세요.
              {' '}🤖 가 계획을 세운 뒤(「처리중」)에는 고칠 수 없고 댓글로 덧붙입니다.
            </div>
            <div className="flex gap-2">
              <button type="button" disabled={busy} onClick={save}
                className="btn btn-primary btn-sm">
                {busy ? '올리는 중…' : isNew ? '요청 올리기' : '저장'}
              </button>
              <button type="button" disabled={busy}
                onClick={() => { if (isNew) requestClose(); else { setEditing(false); setTouched(false); setErr(null); } }}
                className="btn btn-secondary btn-sm">취소</button>
            </div>
          </div>
        )}

        {/* 본문 · 첨부 · 댓글 */}
        {d && !editing && (
          <>
            <div className="rounded border border-slate-200 bg-white px-3 py-2">
              <RequestBody d={d} />
            </div>
            <AttachmentSection mode="live" parentKind="devreq" parentId={String(d.id)} kind={DEVREQ_ATTACH_KIND}
              title="첨부파일" accept={ACCEPT} readOnly={!d.canAttach} />

            <div>
              <div className="text-xs font-bold text-slate-600 mb-1.5">댓글 {d.comments.length}</div>
              <ul className="space-y-2">
                {d.comments.map((c) => (
                  <li key={c.id} className={`rounded border px-3 py-2 ${c.isBot ? 'border-sky-200 bg-sky-50/60' : 'border-slate-200 bg-white'}`}>
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <span className={`font-semibold ${c.isBot ? 'text-sky-800' : 'text-slate-700'}`}>
                        {c.isBot ? '🤖 개발 루틴' : (c.authorName ?? c.author ?? '-')}
                      </span>
                      {c.statusTo && <span className="text-slate-500">→ <StatusChip status={c.statusTo} /></span>}
                      <span className="text-slate-400 ml-auto">{fmtDateTime(c.createdAt)}</span>
                    </div>
                    <CommentText text={c.body} />
                  </li>
                ))}
                {d.comments.length === 0 && <li className="text-xs text-slate-500">아직 댓글이 없습니다 — 다음 검토(평일 2시간마다) 때 🤖 가 계획을 답글로 남깁니다.</li>}
              </ul>
              <div className="mt-2 flex gap-2 items-start">
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={20000}
                  placeholder="덧붙일 말·질문에 대한 답 (Ctrl+Enter 로 남기기)"
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && comment.trim() && !commentM.isPending) commentM.mutate(); }}
                  className="ui-input ui-textarea flex-1" />
                <button type="button" disabled={!comment.trim() || commentM.isPending} onClick={() => commentM.mutate()}
                  className="btn btn-dark btn-sm">
                  {commentM.isPending ? '남기는 중…' : '댓글 남기기'}
                </button>
              </div>
            </div>
            {showBoardLink && (
              <div className="text-right text-xs">
                <Link to={`${DEV_REQUEST_BOARD_PATH}?id=${d.id}`} onClick={onClose} className="text-blue-600 hover:underline">
                  📮 개발요청 게시판에서 보기 →
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </DraggableModal>
  );
}

/** 저장된 본문 — 읽기 전용 편집기로 그린다(서버 HTML 을 심지 않는다 · v2.124.0 원칙) */
function RequestBody({ d }: { d: DevRequestDetail }) {
  const doc = useMemo(() => docForEdit(d.doc, (imageId) => devReqApi.imageUrl(d.id, imageId)), [d]);
  const [v, setV] = useState<RichDoc>(doc);
  useEffect(() => setV(doc), [doc]);
  return <RichEditor label="개발요청" value={v} onChange={setV} readOnly limits={DEV_PLAN_DOC_LIMITS} minHeight={40} />;
}
